import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "inline-block w-2.5 h-2.5 rounded-full mr-2",
  style: { background: 'currentColor' },
  "aria-hidden": "true"
}
const _hoisted_2 = {
  key: 1,
  class: "inline-flex w-5 h-5 mr-1"
}
const _hoisted_3 = /*#__PURE__*/_createElementVNode("i", {
  class: "fas fa-chevron-left",
  "aria-hidden": "true"
}, null, -1)
const _hoisted_4 = { key: 2 }
const _hoisted_5 = {
  key: 3,
  class: "inline-flex w-5 h-5 ml-1"
}
const _hoisted_6 = /*#__PURE__*/_createElementVNode("i", {
  class: "fas fa-chevron-right",
  "aria-hidden": "true"
}, null, -1)

import { computed } from 'vue';

type HierarchyType = 
  | 'primary' 
  | 'secondary-gray' 
  | 'secondary-color' 
  | 'tertiary-color' 
  | 'tertiary-gray' 
  | 'link-color' 
  | 'link-gray'
  | 'primary-destructive'
  | 'secondary-destructive'
  | 'tertiary-destructive'
  | 'link-destructive'
  | 'primary-success'
  | 'secondary-success'
  | 'tertiary-success'
  | 'link-success';

type SizeType = 'sm' | 'md' | 'lg' | 'xl' | '2xl';
type StateType = 'default' | 'hover' | 'focused' | 'disabled';
type IconType = 'default' | 'dot-leading' | 'only';

interface Props {
  as?: string;
  hierarchy?: HierarchyType;
  size?: SizeType;
  state?: StateType;
  icon?: IconType;
  leading?: boolean;
  trailing?: boolean;
  block?: boolean;
}

const roundedClasses = 'rounded-lg';


export default /*#__PURE__*/_defineComponent({
  __name: 'LingoButtonComponent',
  props: {
    as: { default: 'button' },
    hierarchy: { default: 'primary' },
    size: { default: 'sm' },
    state: { default: 'default' },
    icon: { default: 'default' },
    leading: { type: Boolean, default: false },
    trailing: { type: Boolean, default: false },
    block: { type: Boolean, default: false }
  },
  emits: ["click"],
  setup(__props: any, { emit }) {

const props = __props;





const asTag = computed(() => props.as || 'button');
const isButton = computed(() => asTag.value === 'button');
const isDisabled = computed(() => props.state === 'disabled');
const hover = computed(() => props.state === 'hover');
const focused = computed(() => props.state === 'focused');
const iconMode = computed(() => props.icon);

const sizeClasses = computed(() => {
  const sizes = {
    sm: 'px-3 py-2 text-sm gap-2',
    md: 'px-3.5 py-2.5 text-sm gap-2',
    lg: 'px-3.5 py-3 text-base leading-6 gap-2',
    xl: 'px-4 py-3 text-base leading-6 gap-2',
    '2xl': 'px-5 py-4 text-lg leading-7 gap-2'
  };
  return props.icon === 'only' ? 'p-2' : sizes[props.size];
});

const iconModeClass = computed(() => {
  if (props.icon === 'dot-leading') return 'icon-dot-leading';
  if (props.icon === 'only') return 'icon-only';
  return '';
});

const hierarchyClasses = computed(() => {
  const hierarchyMap: Record<HierarchyType, string> = {
    // Primary
    'primary': [
      'bg-[#4f7ed4] text-white border-[#4f7ed4]',
      'hover:bg-[#3065c5] hover:border-[#3065c5]',
      'focus:shadow-[0_0_0_4px_rgba(41,112,255,0.24)]',
      'disabled:bg-[#f2f4f7] disabled:text-[#98a2b3] disabled:border-[#eaecf0]'
    ].join(' '),
    
    // Secondary gray
    'secondary-gray': [
      'bg-white text-[#344054] border-[#d0d5dd]',
      'hover:bg-[#f9fafb] hover:text-[#182230]',
      'focus:shadow-[0_0_0_4px_rgba(152,162,179,0.14)]',
      'disabled:text-[#98a2b3] disabled:border-[#eaecf0] disabled:bg-white'
    ].join(' '),
    
    // Secondary color
    'secondary-color': [
      'bg-white text-[#4f7ed4] border-[#95b1e5]',
      'hover:bg-[#eff3fb] hover:text-[#3065c5]',
      'focus:shadow-[0_0_0_4px_rgba(41,112,255,0.24)]',
      'disabled:text-[#98a2b3] disabled:border-[#eaecf0] disabled:bg-white'
    ].join(' '),
    
    // Tertiary color
    'tertiary-color': [
      'bg-transparent text-[#3065c5] border-transparent',
      'hover:bg-[#eff3fb]',
      'disabled:bg-transparent disabled:text-[#98a2b3] disabled:border-transparent'
    ].join(' '),
    
    // Tertiary gray
    'tertiary-gray': [
      'bg-transparent text-[#475467] border-transparent',
      'hover:bg-[#f9fafb]',
      'disabled:bg-transparent disabled:text-[#98a2b3] disabled:border-transparent'
    ].join(' '),
    
    // Link color
    'link-color': [
      'bg-transparent text-[#3065c5] border-transparent p-0',
      'hover:text-[#4f7ed4]',
      'disabled:text-[#98a2b3]'
    ].join(' '),
    
    // Link gray
    'link-gray': [
      'bg-transparent text-[#475467] border-transparent p-0',
      'hover:text-[#344054]',
      'disabled:text-[#98a2b3]'
    ].join(' '),
    
    // Primary destructive
    'primary-destructive': [
      'bg-[#c12930] text-white border-[#c12930]',
      'hover:bg-[#8f1e24] hover:border-[#8f1e24]',
      'focus:shadow-[0_0_0_4px_rgba(193,41,48,0.24)]',
      'disabled:bg-[#f2f4f7] disabled:text-[#98a2b3] disabled:border-[#eaecf0]'
    ].join(' '),
    
    // Secondary destructive
    'secondary-destructive': [
      'bg-white text-[#8f1e24] border-[#e78d92]',
      'hover:bg-[#fbeaeb] hover:text-[#611518]',
      'focus:shadow-[0_0_0_4px_rgba(193,41,48,0.24)]',
      'disabled:text-[#98a2b3] disabled:border-[#eaecf0] disabled:bg-white'
    ].join(' '),
    
    // Tertiary destructive
    'tertiary-destructive': [
      'bg-transparent text-[#8f1e24] border-transparent',
      'hover:bg-[#fbeaeb] hover:text-[#611518]',
      'disabled:bg-transparent disabled:text-[#98a2b3] disabled:border-transparent'
    ].join(' '),
    
    // Link destructive
    'link-destructive': [
      'bg-transparent text-[#8f1e24] border-transparent p-0',
      'hover:text-[#611518]',
      'disabled:text-[#98a2b3]'
    ].join(' '),
    
    // Primary success
    'primary-success': [
      'bg-[#229354] text-white border-[#229354]',
      'hover:bg-[#145732] hover:border-[#145732]',
      'focus:shadow-[0_0_0_4px_rgba(34,147,84,0.24)]',
      'disabled:bg-[#f2f4f7] disabled:text-[#98a2b3] disabled:border-[#eaecf0]'
    ].join(' '),
    
    // Secondary success
    'secondary-success': [
      'bg-white text-[#145732] border-[#5dda94]',
      'hover:bg-[#e6f9ef] hover:text-[#0d3a21]',
      'focus:shadow-[0_0_0_4px_rgba(34,147,84,0.24)]',
      'disabled:text-[#98a2b3] disabled:border-[#eaecf0] disabled:bg-white'
    ].join(' '),
    
    // Tertiary success
    'tertiary-success': [
      'bg-transparent text-[#145732] border-transparent',
      'hover:bg-[#e6f9ef] hover:text-[#0d3a21]',
      'disabled:bg-transparent disabled:text-[#98a2b3] disabled:border-transparent'
    ].join(' '),
    
    // Link success
    'link-success': [
      'bg-transparent text-[#145732] border-transparent p-0',
      'hover:text-[#0d3a21]',
      'disabled:text-[#98a2b3]'
    ].join(' ')
  };
  
  return hierarchyMap[props.hierarchy];
});

const hasLeading = computed(() => 
  props.icon !== 'dot-leading' && props.icon !== 'only' && props.leading
);

const hasTrailing = computed(() => 
  props.icon !== 'dot-leading' && props.icon !== 'only' && props.trailing
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_resolveDynamicComponent(asTag.value), {
    class: _normalizeClass(["inline-flex items-center justify-center border font-['Poppins'] font-medium leading-5 whitespace-nowrap cursor-pointer no-underline", [
      hierarchyClasses.value,
      sizeClasses.value,
      iconModeClass.value,
      roundedClasses,
      { 
        'w-full flex': _ctx.block, 
        'cursor-not-allowed pointer-events-none': isDisabled.value,
        'focus-ring': focused.value,
        'hover-state': hover.value 
      }
    ]]),
    disabled: isButton.value && isDisabled.value,
    "aria-disabled": isDisabled.value ? 'true' : 'false',
    onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('click')))
  }, {
    default: _withCtx(() => [
      (iconMode.value === 'dot-leading')
        ? (_openBlock(), _createElementBlock("span", _hoisted_1))
        : _createCommentVNode("", true),
      (hasLeading.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_2, [
            _renderSlot(_ctx.$slots, "leading-icon", {}, () => [
              _hoisted_3
            ])
          ]))
        : _createCommentVNode("", true),
      (iconMode.value !== 'only')
        ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
            _renderSlot(_ctx.$slots, "default", {}, () => [
              _createTextVNode("Button CTA")
            ])
          ]))
        : _createCommentVNode("", true),
      (hasTrailing.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_5, [
            _renderSlot(_ctx.$slots, "trailing-icon", {}, () => [
              _hoisted_6
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 3
  }, 8, ["class", "disabled", "aria-disabled"]))
}
}

})